import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, CheckSquare, Square, Filter, X, Check } from 'lucide-react';

interface Document {
  doc_id: string;
  file_name: string;
  sections_count: number;
}

interface DocumentSelectorProps {
  documents: Document[];
  selectedDocIds: string[];
  onSelectionChange: (docIds: string[]) => void;
  onApply?: (docIds: string[]) => void;
  multiSelect?: boolean;
}

const DocumentSelector: React.FC<DocumentSelectorProps> = ({
  documents,
  selectedDocIds,
  onSelectionChange,
  onApply,
  multiSelect = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<string[]>(selectedDocIds);
  const [isApplying, setIsApplying] = useState(false);
  
  // Sync pending selection when selectedDocIds changes externally
  useEffect(() => {
    setPendingSelection(selectedDocIds);
  }, [selectedDocIds]);

  const toggleDocument = (docId: string) => {
    if (multiSelect) {
      if (pendingSelection.includes(docId)) {
        setPendingSelection(pendingSelection.filter(id => id !== docId));
      } else {
        setPendingSelection([...pendingSelection, docId]);
      }
    } else {
      setPendingSelection([docId]);
    }
  };

  const selectAll = () => {
    setPendingSelection(documents.map(d => d.doc_id));
  };

  const clearSelection = () => {
    setPendingSelection([]);
  };

  const handleApply = async () => {
    if (pendingSelection.length === 0) return;
    
    setIsApplying(true);
    try {
      onSelectionChange(pendingSelection);
      if (onApply) {
        await onApply(pendingSelection);
      }
      setIsOpen(false);
    } catch (error) {
      console.error('Error applying selection:', error);
    } finally {
      setIsApplying(false);
    }
  };

  const hasChanges = () => {
    if (pendingSelection.length !== selectedDocIds.length) return true;
    return !pendingSelection.every(id => selectedDocIds.includes(id));
  };

  const getSelectedDocumentsText = () => {
    if (selectedDocIds.length === 0) return 'No documents selected';
    if (selectedDocIds.length === documents.length) return 'All documents';
    if (selectedDocIds.length === 1) {
      const doc = documents.find(d => d.doc_id === selectedDocIds[0]);
      return doc?.file_name || '1 document';
    }
    return `${selectedDocIds.length} documents`;
  };

  return (
    <div className="relative">
      {/* Selector Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-between shadow-lg"
      >
        <div className="flex items-center space-x-2">
          <Filter className="h-5 w-5" />
          <span>{getSelectedDocumentsText()}</span>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </motion.div>
      </motion.button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-2xl overflow-hidden"
          >
            {/* Header with actions */}
            <div className="p-3 bg-gray-50 border-b border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold text-black">
                  Select Documents ({documents.length})
                </span>
                <div className="flex space-x-2">
                  {multiSelect && (
                    <>
                      <button
                        onClick={selectAll}
                        className="text-xs px-3 py-1.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-md text-white font-medium transition-all shadow-sm"
                      >
                        All
                      </button>
                      <button
                        onClick={clearSelection}
                        className="text-xs px-3 py-1.5 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-700 font-medium transition-colors"
                      >
                        Clear
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => {
                      setPendingSelection(selectedDocIds); // Reset to current selection
                      setIsOpen(false);
                    }}
                    className="text-gray-500 hover:text-black transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {/* Apply Button */}
              <motion.button
                onClick={handleApply}
                disabled={!hasChanges() || pendingSelection.length === 0 || isApplying}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                  hasChanges() && pendingSelection.length > 0 && !isApplying
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                whileHover={hasChanges() && pendingSelection.length > 0 && !isApplying ? { scale: 1.02 } : {}}
                whileTap={hasChanges() && pendingSelection.length > 0 && !isApplying ? { scale: 0.98 } : {}}
              >
                {isApplying ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="h-4 w-4 border-2 border-white border-t-transparent rounded-full"
                    />
                    <span>Applying...</span>
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    <span>
                      {pendingSelection.length === 0 
                        ? 'Select at least one document' 
                        : hasChanges() 
                          ? `Apply (${pendingSelection.length} selected)`
                          : 'No changes to apply'}
                    </span>
                  </>
                )}
              </motion.button>
            </div>

            {/* Document List */}
            <div className="max-h-80 overflow-y-auto">
              {documents.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No documents uploaded yet
                </div>
              ) : (
                documents.map((doc) => {
                  const isSelected = pendingSelection.includes(doc.doc_id);
                  return (
                    <motion.div
                      key={doc.doc_id}
                      whileHover={{ backgroundColor: 'rgba(147, 51, 234, 0.05)' }}
                      onClick={() => toggleDocument(doc.doc_id)}
                      className={`p-3 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-center space-x-3 transition-colors ${
                        isSelected ? 'bg-purple-50' : ''
                      }`}
                    >
                      {isSelected ? (
                        <CheckSquare className="h-5 w-5 text-purple-600 flex-shrink-0" />
                      ) : (
                        <Square className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      )}
                      <FileText className="h-5 w-5 text-gray-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold truncate ${
                          isSelected ? 'text-purple-700' : 'text-gray-700'
                        }`}>
                          {doc.file_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {doc.sections_count} section{doc.sections_count !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocumentSelector;

